import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2, Plus, ArrowLeft, Users, Server, CheckCircle, XCircle,
    AlertTriangle, Loader2, Trash2, Crown, RefreshCw,
} from 'lucide-react';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassModal } from '../ui/GlassModal';
import {
    getCompanies, createCompany, getCompany, updateCompany,
    getCompanyMembers, addCompanyMember, removeCompanyMember,
    configureCRMInstance, updateCRMInstance, testCRMInstance,
    deleteCRMInstance,
    Company,
} from '../../services/api';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

type View = 'list' | 'detail';

interface MemberItem {
    user_id: string;
    username: string;
    email: string;
    full_name?: string;
    user_type: string;
    is_admin: boolean;
}

const CRM_STATUS_MAP: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
    active: { variant: 'success', label: 'Active' },
    pending: { variant: 'warning', label: 'Pending' },
    error: { variant: 'danger', label: 'Error' },
};

const CompanyManagement: React.FC = () => {
    const [view, setView] = useState<View>('list');
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Detail view state
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
    const [members, setMembers] = useState<MemberItem[]>([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [companyForm, setCompanyForm] = useState({ name: '', domain: '', plan_tier: 'business', is_active: true });
    const [saving, setSaving] = useState(false);

    // Member add
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [addingMember, setAddingMember] = useState(false);

    // CRM config
    const [crmForm, setCrmForm] = useState({
        crm_url: '', api_client_id: '', api_client_secret: '',
        admin_username: '', admin_password: '', ssl_verify: true, label: '',
        provisioning_endpoint_url: '', provisioning_secret: '', provisioning_enabled: false,
    });
    const [crmTesting, setCrmTesting] = useState(false);
    const [crmTestResult, setCrmTestResult] = useState<{ success: boolean; message: string } | null>(null);
    const [crmSaving, setCrmSaving] = useState(false);

    // Create modal
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', domain: '', plan_tier: 'business' });
    const [creating, setCreating] = useState(false);

    const loadCompanies = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const data = await getCompanies();
            setCompanies(data);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to load companies'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    const openDetail = async (company: Company) => {
        try {
            const full = await getCompany(company.company_id);
            setSelectedCompany(full);
            setCompanyForm({
                name: full.name,
                domain: full.domain || '',
                plan_tier: full.plan_tier,
                is_active: full.is_active,
            });
            if (full.crm_instance) {
                setCrmForm({
                    crm_url: full.crm_instance.crm_url || '',
                    api_client_id: '',
                    api_client_secret: '',
                    admin_username: '',
                    admin_password: '',
                    ssl_verify: full.crm_instance.ssl_verify ?? true,
                    label: full.crm_instance.label || '',
                    provisioning_endpoint_url: full.crm_instance.provisioning_endpoint_url || '',
                    provisioning_secret: '',  // never returned by API
                    provisioning_enabled: full.crm_instance.provisioning_enabled ?? false,
                });
            } else {
                setCrmForm({ crm_url: '', api_client_id: '', api_client_secret: '', admin_username: '', admin_password: '', ssl_verify: true, label: '', provisioning_endpoint_url: '', provisioning_secret: '', provisioning_enabled: false });
            }
            setCrmTestResult(null);
            setView('detail');
            loadMembers(full.company_id);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to load company'));
        }
    };

    const loadMembers = async (companyId: string) => {
        try {
            setMembersLoading(true);
            const data = await getCompanyMembers(companyId);
            setMembers(data);
        } catch {
            setMembers([]);
        } finally {
            setMembersLoading(false);
        }
    };

    const handleSaveCompany = async () => {
        if (!selectedCompany) return;
        try {
            setSaving(true);
            await updateCompany(selectedCompany.company_id, companyForm);
            const updated = await getCompany(selectedCompany.company_id);
            setSelectedCompany(updated);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to update company'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddMember = async () => {
        if (!selectedCompany || !newMemberEmail.trim()) return;
        try {
            setAddingMember(true);
            await addCompanyMember(selectedCompany.company_id, newMemberEmail.trim());
            setNewMemberEmail('');
            loadMembers(selectedCompany.company_id);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to add member'));
        } finally {
            setAddingMember(false);
        }
    };

    const handleRemoveMember = async (userId: string) => {
        if (!selectedCompany || !window.confirm('Remove this member?')) return;
        try {
            await removeCompanyMember(selectedCompany.company_id, userId);
            loadMembers(selectedCompany.company_id);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to remove member'));
        }
    };

    const handleSaveCRM = async () => {
        if (!selectedCompany) return;
        try {
            setCrmSaving(true);
            const payload: Record<string, any> = { ...crmForm };
            // Remove empty optional fields
            Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
            if (selectedCompany.crm_instance) {
                await updateCRMInstance(selectedCompany.company_id, payload);
            } else {
                if (!crmForm.crm_url) { alert('CRM URL is required'); setCrmSaving(false); return; }
                await configureCRMInstance(selectedCompany.company_id, { ...payload, crm_url: crmForm.crm_url });
            }
            const updated = await getCompany(selectedCompany.company_id);
            setSelectedCompany(updated);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to save CRM configuration'));
        } finally {
            setCrmSaving(false);
        }
    };

    const handleTestCRM = async () => {
        if (!selectedCompany) return;
        try {
            setCrmTesting(true);
            setCrmTestResult(null);
            const result = await testCRMInstance(selectedCompany.company_id);
            setCrmTestResult(result);
        } catch (err: any) {
            setCrmTestResult({ success: false, message: getApiError(err, 'Connection test failed') });
        } finally {
            setCrmTesting(false);
        }
    };

    const handleDeleteCRM = async () => {
        if (!selectedCompany || !window.confirm('Remove CRM configuration? This cannot be undone.')) return;
        try {
            await deleteCRMInstance(selectedCompany.company_id);
            const updated = await getCompany(selectedCompany.company_id);
            setSelectedCompany(updated);
            setCrmForm({ crm_url: '', api_client_id: '', api_client_secret: '', admin_username: '', admin_password: '', ssl_verify: true, label: '', provisioning_endpoint_url: '', provisioning_secret: '', provisioning_enabled: false });
            setCrmTestResult(null);
        } catch (err: any) {
            alert(getApiError(err, 'Failed to remove CRM configuration'));
        }
    };

    const handleCreateCompany = async () => {
        if (!createForm.name.trim()) return;
        try {
            setCreating(true);
            await createCompany(createForm);
            setShowCreate(false);
            setCreateForm({ name: '', domain: '', plan_tier: 'business' });
            loadCompanies();
        } catch (err: any) {
            alert(getApiError(err, 'Failed to create company'));
        } finally {
            setCreating(false);
        }
    };

    const goBack = () => {
        setView('list');
        setSelectedCompany(null);
        setMembers([]);
        setCrmTestResult(null);
        loadCompanies();
    };

    // ── Styles ───────────────────────────────────────────────────────
    const sectionStyle: React.CSSProperties = {
        marginBottom: '1.5rem',
    };
    const sectionTitleStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        fontSize: '1rem', fontWeight: 600, color: 'var(--text)',
        marginBottom: '1rem',
    };
    const fieldRowStyle: React.CSSProperties = {
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '0.75rem',
    };
    const memberRowStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0.625rem 0.75rem',
        borderBottom: '1px solid var(--stroke)',
    };
    const memberInfoStyle: React.CSSProperties = {
        display: 'flex', flexDirection: 'column', gap: '0.125rem',
    };
    const memberNameStyle: React.CSSProperties = {
        fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)',
    };
    const memberEmailStyle: React.CSSProperties = {
        fontSize: '0.75rem', color: 'var(--text-secondary)',
    };
    const addMemberRowStyle: React.CSSProperties = {
        display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginTop: '0.75rem',
    };
    const testResultStyle = (success: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.625rem 0.875rem', borderRadius: 'var(--radius-md)',
        background: success ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
        border: `1px solid ${success ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
        color: 'var(--text)', fontSize: '0.875rem', marginTop: '0.75rem',
    });
    const checkboxRowStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.75rem',
        cursor: 'pointer',
    };

    // ── Render: List View ────────────────────────────────────────────
    if (view === 'list') {
        return (
            <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0, color: 'var(--text)', fontSize: '1.125rem' }}>
                        <Building2 size={20} /> Companies
                    </h3>
                    <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowCreate(true)}>
                        New Company
                    </GlassButton>
                </div>

                {error && (
                    <GlassCard padding="md" style={{ marginBottom: '1rem' }}>
                        <p style={{ color: 'var(--danger-500, #ef4444)', margin: 0 }}>{error}</p>
                    </GlassCard>
                )}

                {loading ? (
                    <GlassCard padding="lg">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)' }}>
                            <Loader2 size={18} className="spin" /> Loading companies...
                        </div>
                    </GlassCard>
                ) : companies.length === 0 ? (
                    <GlassCard padding="lg">
                        <p style={{ color: 'var(--text-secondary)', margin: 0, textAlign: 'center' }}>
                            No companies yet. Create one to get started.
                        </p>
                    </GlassCard>
                ) : (
                    <GlassCard padding="none">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '1px solid var(--stroke)' }}>
                                    {['Name', 'Domain', 'Members', 'CRM Status', 'Actions'].map(h => (
                                        <th key={h} style={{
                                            padding: '0.75rem 1rem', textAlign: 'left',
                                            fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)',
                                            textTransform: 'uppercase', letterSpacing: '0.05em',
                                        }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {companies.map(c => {
                                    const crmStatus = CRM_STATUS_MAP[c.crm_status || ''] || { variant: 'default' as const, label: 'Not Configured' };
                                    return (
                                        <tr key={c.company_id} style={{ borderBottom: '1px solid var(--stroke)' }}>
                                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text)', fontWeight: 500 }}>{c.name}</td>
                                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{c.domain || '—'}</td>
                                            <td style={{ padding: '0.75rem 1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{c.member_count ?? 0}</td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <GlassBadge variant={crmStatus.variant}>{crmStatus.label}</GlassBadge>
                                            </td>
                                            <td style={{ padding: '0.75rem 1rem' }}>
                                                <GlassButton variant="ghost" size="sm" onClick={() => openDetail(c)}>
                                                    Edit
                                                </GlassButton>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </GlassCard>
                )}

                {/* Create Company Modal */}
                <GlassModal
                    isOpen={showCreate}
                    onClose={() => setShowCreate(false)}
                    title="Create Company"
                    size="md"
                    footer={
                        <>
                            <GlassButton variant="secondary" onClick={() => setShowCreate(false)}>Cancel</GlassButton>
                            <GlassButton variant="primary" onClick={handleCreateCompany} disabled={creating || !createForm.name.trim()}>
                                {creating ? <><Loader2 size={16} className="spin" /> Creating...</> : 'Create'}
                            </GlassButton>
                        </>
                    }
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <GlassInput
                            label="Company Name"
                            value={createForm.name}
                            onChange={v => setCreateForm({ ...createForm, name: v })}
                            placeholder="Acme Corp"
                            required
                        />
                        <GlassInput
                            label="Domain (optional)"
                            value={createForm.domain}
                            onChange={v => setCreateForm({ ...createForm, domain: v })}
                            placeholder="acme.com"
                        />
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)', marginBottom: '0.375rem' }}>
                                Plan Tier
                            </label>
                            <select
                                value={createForm.plan_tier}
                                onChange={e => setCreateForm({ ...createForm, plan_tier: e.target.value })}
                                style={{
                                    width: '100%', padding: '0.625rem 0.75rem', borderRadius: 'var(--radius-md)',
                                    border: '1px solid var(--stroke)', background: 'var(--panel)',
                                    color: 'var(--text)', fontSize: '0.875rem',
                                }}
                            >
                                <option value="business">Business</option>
                                <option value="enterprise">Enterprise</option>
                            </select>
                        </div>
                    </div>
                </GlassModal>
            </div>
        );
    }

    // ── Render: Detail View ──────────────────────────────────────────
    return (
        <div>
            <div style={{ marginBottom: '1rem' }}>
                <GlassButton variant="ghost" icon={<ArrowLeft size={16} />} onClick={goBack}>
                    Back to Companies
                </GlassButton>
            </div>

            {selectedCompany && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                    {/* Company Info */}
                    <GlassCard padding="md">
                        <div style={sectionTitleStyle}>
                            <Building2 size={18} /> Company Details
                        </div>
                        <div style={fieldRowStyle}>
                            <GlassInput
                                label="Name"
                                value={companyForm.name}
                                onChange={v => setCompanyForm({ ...companyForm, name: v })}
                            />
                            <GlassInput
                                label="Domain"
                                value={companyForm.domain}
                                onChange={v => setCompanyForm({ ...companyForm, domain: v })}
                                placeholder="example.com"
                            />
                        </div>
                        <div style={fieldRowStyle}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text)', marginBottom: '0.375rem' }}>
                                    Plan Tier
                                </label>
                                <select
                                    value={companyForm.plan_tier}
                                    onChange={e => setCompanyForm({ ...companyForm, plan_tier: e.target.value })}
                                    style={{
                                        width: '100%', padding: '0.625rem 0.75rem', borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--stroke)', background: 'var(--panel)',
                                        color: 'var(--text)', fontSize: '0.875rem',
                                    }}
                                >
                                    <option value="business">Business</option>
                                    <option value="enterprise">Enterprise</option>
                                </select>
                            </div>
                            <label style={checkboxRowStyle}>
                                <input
                                    type="checkbox"
                                    checked={companyForm.is_active}
                                    onChange={e => setCompanyForm({ ...companyForm, is_active: e.target.checked })}
                                />
                                Active
                            </label>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                            <GlassButton variant="primary" onClick={handleSaveCompany} disabled={saving}>
                                {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : 'Save Changes'}
                            </GlassButton>
                        </div>
                    </GlassCard>

                    {/* Members */}
                    <GlassCard padding="md">
                        <div style={sectionTitleStyle}>
                            <Users size={18} /> Members
                        </div>
                        {membersLoading ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', padding: '0.5rem 0' }}>
                                <Loader2 size={16} className="spin" /> Loading members...
                            </div>
                        ) : members.length === 0 ? (
                            <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', margin: '0.5rem 0' }}>No members yet.</p>
                        ) : (
                            <div style={{ border: '1px solid var(--stroke)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                {members.map(m => (
                                    <div key={m.user_id} style={memberRowStyle}>
                                        <div style={memberInfoStyle}>
                                            <span style={memberNameStyle}>
                                                {m.full_name || m.username}
                                                {m.is_admin && (
                                                    <Crown size={14} style={{ marginLeft: '0.375rem', color: 'var(--primary-500)', verticalAlign: 'middle' }} />
                                                )}
                                            </span>
                                            <span style={memberEmailStyle}>{m.email} &middot; {m.user_type}</span>
                                        </div>
                                        <GlassButton
                                            variant="ghost"
                                            size="sm"
                                            icon={<Trash2 size={14} />}
                                            onClick={() => handleRemoveMember(m.user_id)}
                                            title="Remove member"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        <div style={addMemberRowStyle}>
                            <div style={{ flex: 1 }}>
                                <GlassInput
                                    label="Add Member by Email"
                                    value={newMemberEmail}
                                    onChange={setNewMemberEmail}
                                    placeholder="user@example.com"
                                />
                            </div>
                            <GlassButton
                                variant="secondary"
                                onClick={handleAddMember}
                                disabled={addingMember || !newMemberEmail.trim()}
                                icon={addingMember ? <Loader2 size={16} className="spin" /> : <Plus size={16} />}
                            >
                                Add
                            </GlassButton>
                        </div>
                    </GlassCard>

                    {/* CRM Configuration */}
                    <GlassCard padding="md">
                        <div style={{ ...sectionTitleStyle, justifyContent: 'space-between' }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <Server size={18} /> CRM Configuration
                            </span>
                            {selectedCompany.crm_instance && (
                                <GlassBadge variant={
                                    selectedCompany.crm_instance.status === 'active' ? 'success' :
                                    selectedCompany.crm_instance.status === 'error' ? 'danger' : 'warning'
                                }>
                                    {selectedCompany.crm_instance.status}
                                </GlassBadge>
                            )}
                        </div>

                        <div style={fieldRowStyle}>
                            <GlassInput
                                label="CRM URL"
                                value={crmForm.crm_url}
                                onChange={v => setCrmForm({ ...crmForm, crm_url: v })}
                                placeholder="https://crm.example.com"
                            />
                            <GlassInput
                                label="Label (optional)"
                                value={crmForm.label}
                                onChange={v => setCrmForm({ ...crmForm, label: v })}
                                placeholder="Production CRM"
                            />
                        </div>
                        <div style={fieldRowStyle}>
                            <GlassInput
                                label="OAuth2 Client ID"
                                value={crmForm.api_client_id}
                                onChange={v => setCrmForm({ ...crmForm, api_client_id: v })}
                                placeholder="Client ID"
                            />
                            <GlassInput
                                label="OAuth2 Client Secret"
                                type="password"
                                value={crmForm.api_client_secret}
                                onChange={v => setCrmForm({ ...crmForm, api_client_secret: v })}
                                placeholder="Client Secret"
                            />
                        </div>
                        <div style={fieldRowStyle}>
                            <GlassInput
                                label="Admin Username"
                                value={crmForm.admin_username}
                                onChange={v => setCrmForm({ ...crmForm, admin_username: v })}
                                placeholder="admin"
                            />
                            <GlassInput
                                label="Admin Password"
                                type="password"
                                value={crmForm.admin_password}
                                onChange={v => setCrmForm({ ...crmForm, admin_password: v })}
                                placeholder="Password"
                            />
                        </div>
                        <label style={checkboxRowStyle}>
                            <input
                                type="checkbox"
                                checked={crmForm.ssl_verify}
                                onChange={e => setCrmForm({ ...crmForm, ssl_verify: e.target.checked })}
                            />
                            Verify SSL Certificate
                        </label>

                        {/* Remote FormIntake provisioning companion endpoint (Phase 5). Secret is
                            Fernet-encrypted server-side and never returned — leave blank to keep the
                            existing one. Enabled is the ops kill-switch. */}
                        <div style={{ borderTop: '1px solid var(--stroke)', margin: '0.5rem 0 0.75rem', paddingTop: '0.75rem' }}>
                            <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                                Remote Provisioning (FormIntake)
                            </div>
                            <div style={fieldRowStyle}>
                                <GlassInput
                                    label="Provisioning Endpoint URL"
                                    value={crmForm.provisioning_endpoint_url}
                                    onChange={v => setCrmForm({ ...crmForm, provisioning_endpoint_url: v })}
                                    placeholder="https://crm.example.com/aime_formintake_provision.php"
                                />
                                <GlassInput
                                    label="Provisioning Secret"
                                    type="password"
                                    value={crmForm.provisioning_secret}
                                    onChange={v => setCrmForm({ ...crmForm, provisioning_secret: v })}
                                    placeholder={selectedCompany.crm_instance?.provisioning_secret_set ? '•••••••• (leave blank to keep)' : '64-char hex secret'}
                                />
                            </div>
                            <label style={checkboxRowStyle}>
                                <input
                                    type="checkbox"
                                    checked={crmForm.provisioning_enabled}
                                    onChange={e => setCrmForm({ ...crmForm, provisioning_enabled: e.target.checked })}
                                />
                                Enable remote provisioning (kill-switch)
                            </label>
                            {selectedCompany.crm_instance?.provisioning_installed_at && (
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem' }}>
                                    Companion installed: {new Date(selectedCompany.crm_instance.provisioning_installed_at).toLocaleString()}
                                </p>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                            <GlassButton variant="primary" onClick={handleSaveCRM} disabled={crmSaving}>
                                {crmSaving ? <><Loader2 size={16} className="spin" /> Saving...</> : 'Save CRM Config'}
                            </GlassButton>
                            {selectedCompany.crm_instance && (
                                <>
                                    <GlassButton
                                        variant="secondary"
                                        onClick={handleTestCRM}
                                        disabled={crmTesting}
                                        icon={crmTesting ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                                    >
                                        Test Connection
                                    </GlassButton>
                                    <GlassButton
                                        variant="danger"
                                        onClick={handleDeleteCRM}
                                        icon={<Trash2 size={16} />}
                                    >
                                        Remove CRM
                                    </GlassButton>
                                </>
                            )}
                        </div>

                        {crmTestResult && (
                            <div style={testResultStyle(crmTestResult.success)}>
                                {crmTestResult.success
                                    ? <CheckCircle size={16} style={{ color: 'rgb(16, 185, 129)' }} />
                                    : <XCircle size={16} style={{ color: 'rgb(239, 68, 68)' }} />}
                                {crmTestResult.message}
                            </div>
                        )}
                    </GlassCard>
                </div>
            )}
        </div>
    );
};

export default CompanyManagement;
